'use client';

import { BRAND, withBase } from '../lib/brand';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { fetchSearch, EMPTY_SEARCH_RESPONSE } from '../lib/searchApi';
import { getSearchSessionId } from '../lib/searchSession';
import type { SearchCard, SiteSearchResponse } from '../types';

const API_BASE = 'https://app.trip-planner.com.au/api';

/**
 * Tell the server the search worked.
 *
 * sendBeacon, not fetch: handleSelect navigates immediately after, and a
 * normal fetch would be cancelled by the unload. Fire and forget — there is
 * nothing useful to do with a failure, and analytics must never be able to
 * interfere with the navigation it is measuring.
 *
 * This is the metric that would have caught #255, #308 and #334 — three
 * separate bugs where selecting a result did nothing — the morning after
 * each shipped rather than whenever someone next noticed by hand.
 */
function reportSearchClick(card: SearchCard, position: number, term: string) {
  try {
    const body = JSON.stringify({
      sessionId: getSearchSessionId(),
      term,
      cardType: card.type,
      cardId: String(card.id ?? ''),
      position,
      surface: 'app-header',
    });
    navigator.sendBeacon(
      `${API_BASE}/search/click`,
      new Blob([body], { type: 'application/json' }),
    );
  } catch {
    /* never break navigation for an analytic */
  }
}

interface SearchDropdownProps {
  query: string;
  isOpen: boolean;
  onClose: () => void;
  // Written with this instance's key handler so the input that owns it can
  // drive arrow keys and Enter. A ref per instance, not a module-level static:
  // Header mounts two of these (desktop + mobile) and a static meant whichever
  // mounted last answered for both.
  keyHandlerRef?: React.MutableRefObject<((e: React.KeyboardEvent) => void) | null>;
}

// Labels for the six card types that get their own section. `brand` is
// deliberately absent — the server pins it first via ranking (see
// search_ranking._BRAND_PIN) and it is capped at one card per query, so a
// dedicated section for it would be noise. It renders unwrapped, above the
// sections, at whatever position the ranking already put it (see below).
const GROUP_LABELS: Record<string, string> = {
  campsite: 'Campsites',
  product: 'Shop',
  article: 'Guides',
  trip: 'Trips',
  route: 'Routes',
  location: 'Places',
};

const DEFAULT_IMAGE = '/images/default-campsite-hero.jpg';

// /api/search is Camping Australia's catalogue: STORE_CHANNEL is hardcoded to
// 'camping-australia' server-side, and product/brand/article cards carry
// storefront paths (/products/…, /collections/…, /blogs/…) that only resolve on
// campingaustralia.com.au. This component renders on BOTH brands — Header.tsx
// mounts it with no BRAND gate — so on escapeto those links would 404.
//
// CA products already leaked into escapeto's dropdown under the old
// /api/unified-search, so that part is not a new regression; brand and article
// are new types and would be. Ask escapeto only for the types that are
// genuinely cross-brand. Campsites and places are country-scoped server-side.
//
// The real fix is brand-awareness in the API rather than a client-side
// allowlist. Recorded as a follow-up; this stops the leak in the meantime.
const SUGGEST_TYPES =
  BRAND.key === 'camping-australia'
    ? undefined
    : ['campsite', 'location', 'trip', 'route'];

const SearchDropdown: React.FC<SearchDropdownProps> = ({ query, isOpen, onClose, keyHandlerRef }) => {
  const [results, setResults] = useState<SiteSearchResponse>(EMPTY_SEARCH_RESPONSE);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // The API returns one ranked list; grouping for display is derived from
  // `type` below. Keyboard navigation walks this flat, ranked list — its
  // order (not visual grouping) is what ArrowUp/ArrowDown step through.
  const flatItems: SearchCard[] = results.results;

  // Debounced fetch
  useEffect(() => {
    if (!isOpen || !query || query.length < 2) {
      setResults(EMPTY_SEARCH_RESPONSE);
      // Must clear loading too. Without this, clearing the input (or closing
      // the dropdown) while a request is in flight leaves loading stuck true
      // and the panel showing "Searching..." with nothing running — a second,
      // independent way to wedge the UI even when the API is perfectly healthy.
      setLoading(false);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    setLoading(true);

    timerRef.current = setTimeout(async () => {
      // finally, not a trailing call: fetchSearch itself never throws, but
      // clearing the loading flag must not depend on that staying true.
      try {
        const data = await fetchSearch(query, {
          mode: 'suggest',
          types: SUGGEST_TYPES,
        });
        setResults(data);
        setSelectedIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, isOpen]);

  // Click outside to close.
  //
  // This listener is per-instance and judges "outside" against this instance's
  // own panel, so `isOpen` MUST be per-instance too. Header once shared one
  // flag across its desktop and mobile mounts: pressing a result in the
  // visible panel was "outside" the hidden one, which closed both on
  // mousedown. The row unmounted before mouseup, so no click event was ever
  // generated on it and nothing opened.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Navigate to the card's own URL — never rebuilt here. Campsite, trip,
  // route and location URLs are in-app (under /apps/campsite-explorer);
  // product, article and brand URLs are storefront paths (see
  // flask-service/search_results.py's _url), so those need a full
  // navigation rather than the Next router.
  // window.location, never router.push — the same trap Header.tsx's
  // handleSearchSubmit already documents. There is no Next `basePath` (only
  // assetPrefix), so the router's table is rooted at "/camp-sites/[campsite]"
  // while the app is served at /apps/campsite-explorer via the Shopify proxy.
  // Pushing the proxied path matched no route and did nothing at all: the
  // dropdown closed, the URL never changed, and no error was logged. A full
  // navigation to that identical URL loads the page fine.
  const handleSelect = useCallback((card: SearchCard) => {
    reportSearchClick(card, flatItems.indexOf(card), query);
    onClose();
    const isAppRoute = card.url.startsWith('/apps/campsite-explorer');
    if (isAppRoute) {
      // withBase re-adds the prefix for camping-australia and the active
      // country segment for escapeto, so strip-then-rebase is intentional.
      window.location.href = withBase(card.url.replace('/apps/campsite-explorer', ''));
    } else {
      window.location.href = `https://campingaustralia.com.au${card.url}`;
    }
  }, [onClose, flatItems, query]);

  // Keyboard handler (called from parent)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || flatItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(flatItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [isOpen, flatItems, selectedIndex, handleSelect, onClose]);

  // Expose the keyboard handler to the input that owns THIS instance.
  useEffect(() => {
    if (!keyHandlerRef) return;
    keyHandlerRef.current = handleKeyDown;
    return () => {
      // Only clear if we are still the current owner — on unmount another
      // instance may already have claimed the ref.
      if (keyHandlerRef.current === handleKeyDown) keyHandlerRef.current = null;
    };
  }, [handleKeyDown, keyHandlerRef]);

  if (!isOpen) return null;

  const hasResults = flatItems.length > 0;

  // Brand is pinned first by the server's ranking, so it simply appears at
  // the top of the list — no reordering here, just pulled out of the
  // type-bucketed sections below so it doesn't get one of its own.
  const brandCards = flatItems.filter(c => c.type === 'brand');
  const grouped = Object.entries(GROUP_LABELS)
    .map(([type, label]) => ({
      type,
      label,
      items: flatItems.filter(c => c.type === type),
    }))
    .filter(g => g.items.length > 0);

  const renderCardItem = (card: SearchCard) => {
    const index = flatItems.indexOf(card);
    const priceRaw = card.meta.price;
    const price = typeof priceRaw === 'number' ? priceRaw : null;
    return (
      <div
        key={`${card.type}-${card.id}`}
        onClick={() => handleSelect(card)}
        onMouseEnter={() => setSelectedIndex(index)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 14px',
          cursor: 'pointer',
          background: selectedIndex === index ? '#f3f4f6' : 'transparent',
          transition: 'background 0.1s',
        }}
      >
        <img
          src={card.image}
          alt=""
          width={40}
          height={40}
          style={{ borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
          loading="lazy"
          // card.image is always populated by the server (a placeholder svg
          // when there's no real photo) — this only guards a dead URL at
          // request time, it is not load-bearing for the common case.
          onError={(e) => { (e.target as HTMLImageElement).src = withBase(DEFAULT_IMAGE); }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {card.title}
          </div>
          {card.subtitle && (
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {card.subtitle}
            </div>
          )}
        </div>
        {price != null && (
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#059669', flexShrink: 0 }}>
            ${price.toFixed(2)}
          </div>
        )}
        <svg width="14" height="14" viewBox="0 0 20 20" fill="#d1d5db" style={{ marginLeft: price != null ? 0 : 'auto', flexShrink: 0 }}>
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
      </div>
    );
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 10000,
        maxHeight: '420px',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {loading && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
          Searching...
        </div>
      )}

      {!loading && !hasResults && query.length >= 2 && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
          {results.degraded
            ? 'Search is unavailable right now.'
            : `No results for "${query}"`}
        </div>
      )}

      {!loading && hasResults && (
        <>
          {results.smartCard && (
            <a
              href={results.smartCard.url}
              style={{
                display: 'block',
                padding: '10px 14px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#1a68d1',
                background: '#eff6ff',
                textDecoration: 'none',
              }}
            >
              {results.smartCard.title}
            </a>
          )}

          {brandCards.map(renderCardItem)}

          {grouped.map((group, groupIdx) => (
            <div key={group.type}>
              <div style={{
                padding: '8px 14px 4px',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#9ca3af',
                borderTop: (groupIdx > 0 || brandCards.length > 0 || !!results.smartCard) ? '1px solid #f3f4f6' : 'none',
              }}>
                {group.label}
              </div>
              {group.items.map(renderCardItem)}
            </div>
          ))}

          <a
            href={`https://campingaustralia.com.au/search?q=${encodeURIComponent(query)}`}
            onClick={onClose}
            style={{
              display: 'block',
              padding: '10px 14px',
              borderTop: '1px solid #f3f4f6',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 600,
              color: '#1a68d1',
              cursor: 'pointer',
              background: '#fafafa',
              borderRadius: '0 0 12px 12px',
              textDecoration: 'none',
            }}
          >
            See all {results.total} results
          </a>
        </>
      )}
    </div>
  );
};

export default SearchDropdown;
